import { PropsWithChildren } from "react";

import { TagSurfaceContext } from "emoreg/atoms/Tag/context";
import { color, spaces } from "emoreg/const";
import { rem } from "polished";
import styled from "styled-components";

import { ModalFooter, ModalHeader } from "./modal-components";

// The modal goes full screen below 480px, the legacy mobile breakpoint. emoreg only defines the
// 1024px one, which would make every modal full screen on tablets.
const MODAL_FULLSCREEN_BREAKPOINT = 480;

const Body = styled.div`
  background: ${color.white};
  border-radius: ${rem(4)};
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  max-height: 90vh;
  width: 100%;
  max-width: ${props => props.maxWidth}px;
  @media (max-width: ${(MODAL_FULLSCREEN_BREAKPOINT - 1) / 16}em) {
    position: relative;
    margin: 0;
    top: 0;
    transform: translate(0, 0);
    height: 100vh;
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background: ${color.white};
  background-clip: padding-box;
  border-radius: ${rem(4)};
  outline: 0;
`;

const ModalBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  padding: ${spaces[24]};
`;

type ModalSubtitleStyle = Pick<React.CSSProperties, "color" | "fontWeight">;

type ModalProps = {
  id?: string;
  title: string | JSX.Element;
  surtitle?: string;
  subtitle?: string | JSX.Element;
  subtitleStyle?: ModalSubtitleStyle;
  headerAction?: JSX.Element;
  footer?: JSX.Element;
  style?: React.CSSProperties;
};

const GenericModal = (props: PropsWithChildren<ModalProps> & { maxWidth: number }) => (
  <TagSurfaceContext.Provider value={undefined}>
    <Body maxWidth={props.maxWidth}>
      <ModalWrapper>
        <ModalHeader
          subtitle={props.subtitle}
          subtitleStyle={props.subtitleStyle}
          title={props.title}
          surtitle={props.surtitle}
          headerAction={props.headerAction}
        />
        <ModalBody id={props.id} style={props.style}>
          {props.children}
        </ModalBody>
        {props.footer && <ModalFooter>{props.footer}</ModalFooter>}
      </ModalWrapper>
    </Body>
  </TagSurfaceContext.Provider>
);

export const Modal = (props: PropsWithChildren<ModalProps>) => (
  <GenericModal {...props} maxWidth={540} />
);

export const LargeModal = (props: PropsWithChildren<ModalProps>) => (
  <GenericModal {...props} maxWidth={900} />
);

export const ExtraLargeModal = (props: PropsWithChildren<ModalProps>) => (
  <GenericModal {...props} maxWidth={1150} />
);
